import React, { useState, useEffect, useRef } from 'react';
import BBALoader from '../../components/BBALoader';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import BackButton from '../../components/BackButton';
import EditButton from '../../components/EditButton';
import SaveButton from '../../components/SaveButton';
import roadmapTemplateService from '../../services/roadmapTemplateService';
import clientRoadmapService from '../../services/clientRoadmapService';
import { getAllCourses } from '../../services/firebase/courses';
import { 
  Building2, 
  Clock, 
  Users, 
  CheckSquare, 
  FileText,
  ArrowLeft,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Link as LinkIcon,
  BookOpen,
  ExternalLink,
  Search,
  Phone,
  ClipboardCopy,
  ClipboardPaste
} from 'lucide-react';
import AddButton from '../../components/AddButton';
import {
  formatDateForInput,
  buildClipboardSnapshot,
  writeClipboardSnapshot,
  readClipboardSnapshot,
  applyClipboardPayloadToTemplate,
} from '../../utils/roadmapTemplateClipboard';
import {
  PORTAL_PAGE_GROUPS,
  getPortalPageLabel,
  resolvePortalPagePath,
} from '../../data/portalPageOptions';
import {
  TEMPLATE_STATUSES,
  CLIENT_ROADMAP_STATUSES,
  DEFAULT_ROADMAP_STATUS,
  normalizeTemplateStatus,
  normalizeClientRoadmapStatus,
  getRoadmapStatusLabel,
} from '../../utils/roadmapStatus';
import PortalBackground from '../../components/PortalBackground';

const RoadmapTemplateOverview = () => {
  const { templateId, clientId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const isClientContext = !!clientId;
  // Templates only carry Not Started / In Progress / Hidden — 'Completed' is a client-roadmap state
  const statusOptions = isClientContext ? CLIENT_ROADMAP_STATUSES : TEMPLATE_STATUSES;
  const normalizeStatus = isClientContext ? normalizeClientRoadmapStatus : normalizeTemplateStatus;

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    department: '',
    order: 1,
    emoji: '',
    status: DEFAULT_ROADMAP_STATUS,
    dateStarted: '',
    dateCompleted: ''
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    courseId: '',
    courseTitle: '',
    portalPage: '',
    externalLink: '',
    callBookingDepartment: ''
  });
  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [portalPageSearch, setPortalPageSearch] = useState('');
  const [showPortalPageDropdown, setShowPortalPageDropdown] = useState(false);
  const [showLinkMenu, setShowLinkMenu] = useState(null);
  const linkMenuRef = useRef(null);

  const departments = [
    { id: 'all', name: 'All', icon: null },
    { id: 'general', name: 'General', icon: null },
    { id: 'sales', name: 'Sales', icon: null },
    { id: 'operations', name: 'Operations', icon: null },
    { id: 'finance', name: 'Finance', icon: null },
    { id: 'technology', name: 'Technology', icon: null },
    { id: 'marketing', name: 'Marketing', icon: null },
    { id: 'hr', name: 'HR', icon: null }
  ];

  // Popular emojis for templates
  const popularEmojis = [
    // Business & Work
    '🚀', '⚡', '🔥', '💡', '🎯', '📈', '🔄', '⚙️', '🎨', '📱',
    '💻', '🌐', '📊', '🎪', '🏆', '💎', '🌟', '🎭', '📝', '📋',
    '📅', '⏰', '🔔', '📧', '💬', '📞', '📹', '🎵', '🛒', '💰',
    '💳', '📦', '🚚', '🏢', '👥', '👤', '🎓', '🔐', '🔍', '📌',
    '📍', '🤖', '🧠', '🔧', '🔨', '🛠️', '✅', '❌', '⚠️', 'ℹ️',
    '📚', '📖', '✏️', '📐', '🧮', '🔬', '🧪', '🔭', '📡', '🛰️',
    '🎡', '🎢', '🎠', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷',
    '🎺', '🎸', '🎻', '🎲', '🎯', '🎳', '🎮', '🎰', '🧩', '📷',
    '📸', '📹', '📺', '📻', '📟', '📠', '☎️', '💡', '🔦', '🕯️',
    '🪔', '🧯', '💸', '💵', '🪙', '⚖️', '🪜', '🦯', '🦽', '🦼',
    // More Business & Productivity
    '📑', '📄', '📃', '📜', '📰', '🗞️', '📓', '📔', '📒', '📕',
    '📗', '📘', '📙', '📚', '📖', '🔖', '🏷️', '💰', '💴', '💵',
    '💶', '💷', '💸', '💳', '🧾', '💹', '📊', '📈', '📉', '📇',
    '📋', '📁', '📂', '🗂️', '🗄️', '🗃️', '📅', '📆', '🗓️', '📆',
    '🗒️', '🗓️', '📆', '📅', '🗓️', '📆', '📅', '🗓️', '📆', '📅',
    // Communication & Social
    '💌', '📧', '📨', '📩', '📤', '📥', '📦', '📫', '📪', '📬',
    '📭', '📮', '🗳️', '✉️', '📧', '📨', '📩', '📤', '📥', '📦',
    '💬', '💭', '🗯️', '🗨️', '👁️', '👁️‍🗨️', '🗣️', '👤', '👥', '👶',
    '🧒', '👦', '👧', '🧑', '👨', '👩', '🧓', '👴', '👵', '🙍',
    '🙎', '🙅', '🙆', '💁', '🙋', '🧏', '🙇', '🤦', '🤷', '👮',
    // Technology & Devices
    '⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️',
    '🗜️', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '📺', '📻',
    '🎙️', '🎚️', '🎛️', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡',
    '🔋', '🔌', '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💵',
    // Objects & Tools
    '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🔩', '⚙️', '🧰', '🧲', '🔫',
    '💣', '🧨', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '⚱️', '🏺',
    '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🧬', '🦠', '🧫',
    '🧪', '🌡️', '🧹', '🧺', '🧻', '🚽', '🚿', '🛁', '🛀', '🧼',
    '🧽', '🧴', '🧷', '🧶', '🧵', '🧥', '🥼', '🦺', '👚', '👕',
    // Food & Drink
    '🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍈', '🍒', '🍑',
    '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒',
    '🌶️', '🌽', '🥕', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨',
    '🧀', '🥚', '🍳', '🥞', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭',
    '🍔', '🍟', '🍕', '🥪', '🥙', '🌮', '🌯', '🥗', '🥘', '🥫',
    '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🦪', '🍤', '🍙',
    '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡', '🍧', '🍨', '🍦',
    '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩',
    '🍪', '🌰', '🥜', '🍯', '🥛', '🍼', '☕', '🍵', '🧃', '🥤',
    '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹', '🧉', '🍾',
    // Activities & Sports
    '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
    '🏓', '🏸', '🥅', '🏒', '🏑', '🏏', '🥃', '⛳', '🏹', '🎣',
    '🥊', '🥋', '🎽', '🛹', '🛷', '⛸️', '🥌', '🎿', '⛷️', '🏂',
    '🏋️', '🤼', '🤸', '🤺', '⛹️', '🤹', '🧘', '🏄', '🏊', '🤽',
    '🚣', '🧗', '🚵', '🚴', '🏇', '🛶', '⛵', '帆', '🚤', '🛥️',
    '🛳️', '⛴️', '🚢', '⚓', '⛽', '🚧', '🚦', '🚥', '🗺️', '🗿',
    // Transportation - Planes, Trucks, Vehicles
    '✈️', '🛫', '🛬', '🛩️', '💺', '🚁', '🛸', '🚀', '🛰️', '🚂',
    '🚃', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞',
    '🚟', '🚠', '🚡', '🚋', '🚌', '🚍', '🚎', '🚐', '🚑', '🚒',
    '🚓', '🚔', '🚕', '🚖', '🚗', '🚘', '🚙', '🚚', '🚛', '🚜',
    '🏎️', '🏍️', '🛵', '🦽', '🦼', '🛴', '🚲', '🛺', '🛹', '🛼',
    '🚁', '🛶', '⛵', '🚤', '🛥️', '🛳️', '⛴️', '🚢', '⚓', '🚧',
    '🗽', '🗼', '🏰', '🏯', '🏟️', '🎡', '🎢', '🎠', '⛲', '⛱️',
    '🏖️', '🏝️', '🏜️', '🌋', '⛰️', '🏔️', '🗻', '🏕️', '⛺', '🏠',
    '🏡', '🏘️', '🏚️', '🏗️', '🏭', '🏢', '🏬', '🏣', '🏤', '🏥',
    '🏦', '🏨', '🏪', '🏫', '🏩', '💒', '🏛️', '⛪', '🕌', '🕍',
    '🕋', '⛩️', '🛤️', '🛣️', '🗾', '🎑', '🏞️', '🌅', '🌄', '🌠',
    '🌌', '⛅', '🌤️', '⛅', '🌥️', '☁️', '⛈️', '🌩️', '🌧️', '⛈️',
    '🌦️', '🌦️', '☀️', '🌞', '⭐', '🌟', '💫', '✨', '⚡', '☄️',
    '💥', '🔥', '🌈', '☔', '❄️', '☃️', '⛄', '🌊', '💧', '💦',
    // Symbols & Signs
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
    '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐',
    '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐',
    '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳',
    '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️',
    '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️',
    '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️',
    '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❓', '❕',
    '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️',
    '🔰', '♻️', '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠',
    'Ⓜ️', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🈳', '🈂️', '🛂',
    '🛃', '🛄', '🛅', '🚹', '🚺', '🚼', '🚻', '🚮', '🎦', '📶',
    '🈁', '🔣', 'ℹ️', '🔤', '🔡', '🔠', '🆖', '🆗', '🆙', '🆒',
    '🆕', '🆓', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣',
    '8️⃣', '9️⃣', '🔟', '🔢', '#️⃣', '*️⃣', '▶️', '⏸️', '⏯️', '⏹️',
    '⏺️', '⏭️', '⏮️', '⏩', '⏪', '⏫', '⏬', '◀️', '🔼', '🔽',
    '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️',
    '↪️', '↩️', '⤴️', '⤵️', '🔀', '🔁', '🔂', '🔄', '🔃', '🎵',
    '🎶', '➕', '➖', '➗', '✖️', '💲', '💱', '™️', '©️', '®️',
    '〰️', '➰', '➿', '🔚', '🔙', '🔛', '🔜', '🔝', '✔️', '☑️',
    '🔘', '⚪', '⚫', '🔴', '🔵', '🟠', '🟡', '🟡', '🟢', '🟣',
    '🟤', '⚫', '🔶', '🔷', '🔸', '🔹', '🔺', '🔻', '💠', '🔳',
    '🔲', '▪️', '▫️', '◾', '◽', '◼️', '◻️', '🟥', '🟧', '🟨',
    '🟩', '🟦', '🟪', '🟫', '⬛', '⬜', '🔈', '🔇', '🔉', '🔊',
    '🔔', '🔕', '📣', '📢', '👁️‍🗨️', '💬', '💭', '🗯️', '♠️', '♣️',
    '♥️', '♦️', '🃏', '🎴', '🀄', '🕐', '🕑', '🕒', '🕓', '🕔',
    '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛', '🕜', '🕝', '🕞',
    '🕟', '🕠', '🕡', '🕢', '🕣', '🕤', '🕥', '🕦', '🕧'
  ];

  const filteredEmojis = popularEmojis.filter(emoji => 
    emojiSearch === '' || emoji.includes(emojiSearch)
  );


  useEffect(() => {
    const loadTemplate = async () => {
      try {
        let loadedTemplate = null;
        
        if (isClientContext) {
          // Load from client roadmap
          const roadmap = await clientRoadmapService.getClientRoadmap(clientId);
          const templateIdNum = parseFloat(templateId);
          loadedTemplate = roadmap.find(t => 
            t.clientRoadmapId === templateIdNum || 
            t.clientRoadmapId === templateId ||
            t.id === templateIdNum ||
            t.id === templateId
          );
        } else {
          // Load from global templates
          loadedTemplate = await roadmapTemplateService.getTemplateById(templateId);
        }
        
        if (loadedTemplate) {
          // Ensure tasks have order field and sort them
          const tasksWithOrder = (loadedTemplate.tasks || []).map((task, index) => ({
            ...task,
            order: task.order !== undefined ? task.order : index + 1
          })).sort((a, b) => (a.order || 0) - (b.order || 0));
          
          const templateWithOrderedTasks = {
            ...loadedTemplate,
            tasks: tasksWithOrder
          };
          
          setTemplate(templateWithOrderedTasks);

          setEditForm({
            title: loadedTemplate.title || '',
            description: loadedTemplate.description || '',
            department: loadedTemplate.department || '',
            order: loadedTemplate.order || loadedTemplate.allOrder || 1,
            emoji: loadedTemplate.emoji || '',
            status: isClientContext
              ? normalizeClientRoadmapStatus(loadedTemplate.status)
              : normalizeTemplateStatus(loadedTemplate.status),
            dateStarted: formatDateForInput(loadedTemplate.dateStarted),
            dateCompleted: formatDateForInput(loadedTemplate.dateCompleted)
          });
        } else {
          console.error('Template not found');
        }
      } catch (error) {
        console.error('Error loading template:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [templateId, clientId, isClientContext]);

  // Load courses
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const fetchedCourses = await getAllCourses();
        setCourses(fetchedCourses);
      } catch (error) {
        console.error('Error loading courses:', error);
      }
    };
    loadCourses();
  }, []);

  // Close link menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (linkMenuRef.current && !linkMenuRef.current.contains(event.target)) {
        setShowLinkMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSave = async () => {
    if (!template) return;

    const updatedTemplate = {
      ...template,
      ...editForm
    };
    
    // Remove old order fields if they exist
    if (updatedTemplate.allOrder) delete updatedTemplate.allOrder;
    if (updatedTemplate.categoryOrder) delete updatedTemplate.categoryOrder;

    if (isClientContext) {
      // Save to client roadmap
      const templateIdToUse = template.clientRoadmapId || template.id;
      await clientRoadmapService.updateTemplateInClientRoadmap(clientId, templateIdToUse, updatedTemplate);
    } else {
      // Save to global templates
      await roadmapTemplateService.updateTemplate(template.id, updatedTemplate);
    }
    
    setTemplate(updatedTemplate);
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (template) {
      setEditForm({
        title: template.title || '',
        description: template.description || '',
        department: template.department || '',
        order: template.order || template.allOrder || 1,
        emoji: template.emoji || '',
        status: normalizeStatus(template.status),
        dateStarted: formatDateForInput(template.dateStarted),
        dateCompleted: formatDateForInput(template.dateCompleted),
      });
    }
    setIsEditing(false);
  };

  const handleCopyTemplate = () => {
    if (!template) return;
    writeClipboardSnapshot(buildClipboardSnapshot(template, editForm, isEditing));
  };

  const handlePasteTemplate = () => {
    const snap = readClipboardSnapshot();
    if (!template) return;
    if (!snap?.payload) {
      window.alert('No roadmap template in clipboard. Use Copy on a roadmap template first.');
      return;
    }
    const merged = applyClipboardPayloadToTemplate(template, snap.payload);
    delete merged.allOrder;
    delete merged.categoryOrder;
    merged.tasks = [...(merged.tasks || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    setTemplate(merged);
    setEditForm({
      title: merged.title || '',
      description: merged.description || '',
      department: merged.department || '',
      order: merged.order || merged.allOrder || 1,
      emoji: merged.emoji || '',
      status: normalizeStatus(merged.status),
      dateStarted: '',
      dateCompleted: '',
    });
    setIsEditing(true);
  };

  const handleDelete = async () => {
    if (!template) return;
    
    if (window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      if (isClientContext) {
        const templateIdToUse = template.clientRoadmapId || template.id;
        await clientRoadmapService.deleteTemplateFromClientRoadmap(clientId, templateIdToUse);
        navigate(`/crm/fulfillment/clientroadmaps/${clientId}`);
      } else {
        await roadmapTemplateService.deleteTemplate(template.id);
        navigate('/crm/roadmap-templates');
      }
    }
  };

  const handleAddTask = () => {
    setTaskForm({
      title: '',
      description: '',
      courseId: '',
      courseTitle: '',
      portalPage: '',
      externalLink: '',
      callBookingDepartment: ''
    });
    setCourseSearch('');
    setPortalPageSearch('');
    setEditingTaskIndex(null);
    setShowTaskForm(true);
  };

  const handleEditTask = (taskIndex) => {
    const task = template.tasks[taskIndex];
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      courseId: task.courseId || '',
      courseTitle: task.courseTitle || '',
      // Normalize legacy values (no leading slash / retired pages) on edit
      portalPage: resolvePortalPagePath(task.portalPage) || '',
      externalLink: task.externalLink || '',
      callBookingDepartment: task.callBookingDepartment || ''
    });
    setCourseSearch(task.courseTitle || '');
    setPortalPageSearch(getPortalPageLabel(task.portalPage));
    setEditingTaskIndex(taskIndex);
    setShowTaskForm(true);
  };

  const handleSaveTask = async () => {
    if (!template) return;

    const existingTasks = template.tasks || [];
    const maxOrder = existingTasks.length > 0 
      ? Math.max(...existingTasks.map(t => t.order || 0), 0)
      : 0;

    const newTask = {
      id: editingTaskIndex !== null ? template.tasks[editingTaskIndex].id : Date.now(),
      title: taskForm.title,
      description: taskForm.description,
      courseId: taskForm.courseId || '',
      courseTitle: taskForm.courseTitle || '',
      portalPage: taskForm.portalPage || '',
      externalLink: taskForm.externalLink || '',
      callBookingDepartment: taskForm.callBookingDepartment || '',
      order: editingTaskIndex !== null 
        ? template.tasks[editingTaskIndex].order 
        : maxOrder + 1,
      completed: false,
      files: [],
      dueDate: null,
      assignees: [],
      accountId: 'internal',
      createdBy: null,
      createdAt: null,
      updatedAt: null,
      completedAt: null,
      status: 'Not Done'
    };

    const updatedTasks = [...existingTasks];
    if (editingTaskIndex !== null) {
      updatedTasks[editingTaskIndex] = newTask;
    } else {
      updatedTasks.push(newTask);
    }

    // Sort by order
    updatedTasks.sort((a, b) => (a.order || 0) - (b.order || 0));

    const updatedTemplate = {
      ...template,
      tasks: updatedTasks
    };

    if (isClientContext) {
      const templateIdToUse = template.clientRoadmapId || template.id;
      await clientRoadmapService.updateTemplateInClientRoadmap(clientId, templateIdToUse, updatedTemplate);
    } else {
      roadmapTemplateService.updateTemplate(template.id, updatedTemplate);
    }
    
    setTemplate(updatedTemplate);
    setShowTaskForm(false);
    setEditingTaskIndex(null);
    setTaskForm({
      title: '',
      description: ''
    });
  };

  const handleDeleteTask = async (taskIndex) => {
    if (!template || !window.confirm('Are you sure you want to delete this task?')) return;

    const updatedTasks = template.tasks.filter((_, index) => index !== taskIndex);
    // Reorder remaining tasks
    updatedTasks.forEach((task, index) => {
      task.order = index + 1;
    });

    const updatedTemplate = {
      ...template,
      tasks: updatedTasks
    };

    if (isClientContext) {
      const templateIdToUse = template.clientRoadmapId || template.id;
      await clientRoadmapService.updateTemplateInClientRoadmap(clientId, templateIdToUse, updatedTemplate);
    } else {
      roadmapTemplateService.updateTemplate(template.id, updatedTemplate);
    }
    
    setTemplate(updatedTemplate);
  };

  const handleMoveTask = async (taskIndex, direction) => {
    if (!template) return;

    const updatedTasks = [...template.tasks];
    const newIndex = direction === 'up' ? taskIndex - 1 : taskIndex + 1;

    if (newIndex < 0 || newIndex >= updatedTasks.length) return;

    // Swap tasks
    [updatedTasks[taskIndex], updatedTasks[newIndex]] = [updatedTasks[newIndex], updatedTasks[taskIndex]];

    // Update order values
    updatedTasks.forEach((task, index) => {
      task.order = index + 1;
    });

    const updatedTemplate = {
      ...template,
      tasks: updatedTasks
    };

    if (isClientContext) {
      const templateIdToUse = template.clientRoadmapId || template.id;
      await clientRoadmapService.updateTemplateInClientRoadmap(clientId, templateIdToUse, updatedTemplate);
    } else {
      roadmapTemplateService.updateTemplate(template.id, updatedTemplate);
    }
    
    setTemplate(updatedTemplate);
  };

  const handleCancelTask = () => {
    setShowTaskForm(false);
    setEditingTaskIndex(null);
    setTaskForm({
      title: '',
      description: '',
      courseId: '',
      courseTitle: '',
      portalPage: '',
      externalLink: '',
      callBookingDepartment: ''
    });
    setCourseSearch('');
    setPortalPageSearch('');
  };

  const filteredCourses = courses.filter(course =>
    course.title?.toLowerCase().includes(courseSearch.toLowerCase()) ||
    course.description?.toLowerCase().includes(courseSearch.toLowerCase()) ||
    course.category?.toLowerCase().includes(courseSearch.toLowerCase())
  );

  // Grouped portal page results — a group is kept when its name matches, or with
  // only the options that match the search text. While the box still shows the
  // label of the current selection, treat it as an empty query so reopening the
  // dropdown shows the full list rather than nothing.
  const selectedPortalPageLabel = getPortalPageLabel(taskForm.portalPage);
  const portalPageQuery =
    portalPageSearch.trim() === selectedPortalPageLabel ? '' : portalPageSearch.trim().toLowerCase();
  const filteredPortalPageGroups = PORTAL_PAGE_GROUPS.map(({ group, options }) => {
    if (!portalPageQuery || group.toLowerCase().includes(portalPageQuery)) {
      return { group, options };
    }
    return {
      group,
      options: options.filter(
        (page) =>
          page.label.toLowerCase().includes(portalPageQuery) ||
          page.value.toLowerCase().includes(portalPageQuery)
      ),
    };
  }).filter(({ options }) => options.length > 0);
  const portalPageResultCount = filteredPortalPageGroups.reduce(
    (sum, { options }) => sum + options.length,
    0
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <BBALoader size={48} />
          </div>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Template Not Found</h2>
            <BackButton onClick={() => navigate(isClientContext ? `/crm/fulfillment/clientroadmaps/${clientId}` : '/crm/roadmap-templates')} />
          </div>
        </div>
      </div>
    );
  }

  const departmentName = departments.find(d => d.id === template.department)?.name || template.department;

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-3 items-center mb-8">
          <div className="flex justify-start">
            <BackButton onClick={() => navigate(isClientContext ? `/crm/fulfillment/clientroadmaps/${clientId}` : '/crm/roadmap-templates')} />
          </div>
          <div className="flex justify-center items-center space-x-3">
            {!isEditing && template.emoji && (
              <span className="text-4xl">{template.emoji}</span>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white text-center">
              {isEditing ? (
                <input
                  type="text"
                  className="text-center bg-transparent border-b-2 border-gray-300 dark:border-gray-600 focus:border-red-500 focus:outline-none text-gray-900 dark:text-white"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                />
              ) : (
                template.title
              )}
            </h1>
          </div>
          <div className="flex justify-end flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={handleCopyTemplate}
              title="Copy all template fields, tasks, and navigators"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${
                darkMode
                  ? 'bg-gray-700 text-gray-100 hover:bg-gray-600 border border-gray-600'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300'
              }`}
            >
              <ClipboardCopy className="w-4 h-4 shrink-0" aria-hidden />
              Copy
            </button>
            <button
              type="button"
              onClick={handlePasteTemplate}
              title="Paste copied template from clipboard (works across browser tabs)"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${
                darkMode
                  ? 'bg-gray-700 text-gray-100 hover:bg-gray-600 border border-gray-600'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300'
              }`}
            >
              <ClipboardPaste className="w-4 h-4 shrink-0" aria-hidden />
              Paste
            </button>
            {isEditing ? (
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex items-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
                <SaveButton onClick={handleSave} />
              </div>
            ) : (
              <EditButton onClick={() => setIsEditing(true)} />
            )}
          </div>
        </div>

        {/* Template Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Description */}
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <div className="flex items-start space-x-4 mb-4">
              <FileText className="w-6 h-6 text-blue-500 dark:text-blue-400 mt-1" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Description</h2>
                {isEditing ? (
                  <textarea
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                    rows={4}
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                ) : (
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {template.description || 'No description provided'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Department */}
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <div className="flex items-start space-x-4 mb-4">
              <Building2 className="w-6 h-6 text-blue-500 dark:text-blue-400 mt-1" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Department</h2>
                {isEditing ? (
                  <select
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                    value={editForm.department}
                    onChange={(e) => setEditForm(prev => ({ ...prev, department: e.target.value }))}
                  >
                    {departments.filter(d => d.id !== 'all').map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{departmentName}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Order, Emoji, Status — full-width row (each ~1/3 on md+) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Order */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <div className="flex items-start space-x-4 mb-4">
                <Clock className="w-6 h-6 text-blue-500 dark:text-blue-400 mt-1" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Order</h2>
                  {isEditing ? (
                    <input
                      type="number"
                      className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                      value={editForm.order}
                      onChange={(e) => setEditForm(prev => ({ ...prev, order: parseInt(e.target.value) || 1 }))}
                      min="1"
                    />
                  ) : (
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Position {template.order || template.allOrder || 1}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Emoji */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <div className="flex items-start space-x-4 mb-4">
                <span className="text-2xl mt-1">😊</span>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Emoji</h2>
                  {isEditing ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        {editForm.emoji && (
                          <span className="text-3xl">{editForm.emoji}</span>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowEmojiPicker(true)}
                          className={`px-4 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-900 hover:bg-gray-50'} rounded-md`}
                        >
                          {editForm.emoji ? 'Change Emoji' : 'Select Emoji'}
                        </button>
                        {editForm.emoji && (
                          <button
                            type="button"
                            onClick={() => setEditForm(prev => ({ ...prev, emoji: '' }))}
                            className="px-3 py-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      {template.emoji ? (
                        <span className="text-3xl">{template.emoji}</span>
                      ) : (
                        <span className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No emoji selected</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Status */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <div className="flex items-start space-x-4 mb-4">
                <CheckSquare className="w-6 h-6 text-blue-500 dark:text-blue-400 mt-1" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Status</h2>
                  {isEditing ? (
                    <div className="space-y-3">
                      <select
                        className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                        value={editForm.status}
                        onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      {!isClientContext && (
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          New client roadmaps start each card at the status set here.
                        </p>
                      )}
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Date Started
                        </label>
                        <input
                          type="date"
                          className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                          value={editForm.dateStarted}
                          onChange={(e) => setEditForm(prev => ({ ...prev, dateStarted: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Date Completed
                        </label>
                        <input
                          type="date"
                          className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                          value={editForm.dateCompleted}
                          onChange={(e) => setEditForm(prev => ({ ...prev, dateCompleted: e.target.value }))}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          normalizeStatus(template.status) === 'completed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : normalizeStatus(template.status) === 'in-progress'
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                            : normalizeStatus(template.status) === 'hidden'
                            ? 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-dashed border-gray-400 dark:border-gray-500'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}>
                          {getRoadmapStatusLabel(normalizeStatus(template.status))}
                        </span>
                      </div>
                      {template.dateStarted && (
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <span className="font-medium">Started:</span> {
                            template.dateStarted.toDate 
                              ? template.dateStarted.toDate().toLocaleDateString()
                              : new Date(template.dateStarted).toLocaleDateString()
                          }
                        </div>
                      )}
                      {template.dateCompleted && (
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <span className="font-medium">Completed:</span> {
                            template.dateCompleted.toDate 
                              ? template.dateCompleted.toDate().toLocaleDateString()
                              : new Date(template.dateCompleted).toLocaleDateString()
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>

        {/* Navigators */}
        {template.navigators && template.navigators.length > 0 && (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-8`}>
            <div className="flex items-start space-x-4 mb-4">
              <Users className="w-6 h-6 text-blue-500 dark:text-blue-400 mt-1" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Navigators</h2>
                <div className="space-y-2">
                  {template.navigators.map((navigator, index) => (
                    <div key={index} className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3`}>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {navigator.name}
                      </p>
                      {navigator.role && (
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {navigator.role}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tasks - Full Width */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-8`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-4 flex-1">
                <CheckSquare className="w-6 h-6 text-blue-500 dark:text-blue-400 mt-1" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tasks</h2>
                </div>
              </div>
              <AddButton onClick={handleAddTask} />
            </div>

            {/* Task Form */}
            {showTaskForm && (
              <div className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 mb-4 border-2 border-blue-500`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {editingTaskIndex !== null ? 'Edit Task' : 'Add New Task'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Title *
                    </label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                      value={taskForm.title}
                      onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter task title"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Description
                    </label>
                    <textarea
                      className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                      rows={3}
                      value={taskForm.description}
                      onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter task description"
                    />
                  </div>

                  {/* Course Link */}
                  {/* Links Section with Plus Icon */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Links
                    </label>
                    <div className="space-y-3">
                      {/* Course Link */}
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <BookOpen className="w-4 h-4 text-green-500" />
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Course</span>
                        </div>
                        <div className="relative">
                          <input
                            type="text"
                            className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md pr-10`}
                            value={courseSearch}
                            onChange={(e) => {
                              setCourseSearch(e.target.value);
                              setShowCourseDropdown(true);
                            }}
                            onFocus={() => setShowCourseDropdown(true)}
                            onBlur={() => {
                              setTimeout(() => setShowCourseDropdown(false), 200);
                            }}
                            placeholder="Type to search courses..."
                          />
                          <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                          {taskForm.courseTitle && (
                            <button
                              onClick={() => {
                                setTaskForm(prev => ({ ...prev, courseId: '', courseTitle: '' }));
                                setCourseSearch('');
                              }}
                              className="absolute right-8 top-2 text-gray-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          {showCourseDropdown && (courseSearch || filteredCourses.length > 0) && (
                            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {filteredCourses.length > 0 ? (
                                filteredCourses.map((course) => (
                                  <div
                                    key={course.id}
                                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                                    onClick={() => {
                                      setCourseSearch(course.title);
                                      setTaskForm(prev => ({
                                        ...prev,
                                        courseId: course.id,
                                        courseTitle: course.title
                                      }));
                                      setShowCourseDropdown(false);
                                    }}
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white">{course.title}</div>
                                    {course.category && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400">{course.category}</div>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                  No courses found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Portal Page Link */}
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <ExternalLink className="w-4 h-4 text-purple-500" />
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Portal Page</span>
                        </div>
                        <div className="relative">
                          <input
                            type="text"
                            className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md pr-10`}
                            value={portalPageSearch}
                            onChange={(e) => {
                              setPortalPageSearch(e.target.value);
                              setShowPortalPageDropdown(true);
                            }}
                            onFocus={() => setShowPortalPageDropdown(true)}
                            onBlur={() => {
                              setTimeout(() => setShowPortalPageDropdown(false), 200);
                            }}
                            placeholder="Type to search portal pages..."
                          />
                          <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                          {taskForm.portalPage && (
                            <button
                              onClick={() => {
                                setTaskForm(prev => ({ ...prev, portalPage: '' }));
                                setPortalPageSearch('');
                              }}
                              className="absolute right-8 top-2 text-gray-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          {showPortalPageDropdown && portalPageResultCount > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {filteredPortalPageGroups.map(({ group, options }) => (
                                <div key={group}>
                                  <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 sticky top-0">
                                    {group}
                                  </div>
                                  {options.map((page) => (
                                    <div
                                      key={page.value}
                                      className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                                      onClick={() => {
                                        setPortalPageSearch(
                                          group === 'General' ? page.label : `${group} · ${page.label}`
                                        );
                                        setTaskForm(prev => ({ ...prev, portalPage: page.value }));
                                        setShowPortalPageDropdown(false);
                                      }}
                                    >
                                      <div className="font-medium text-gray-900 dark:text-white">
                                        {page.emoji ? `${page.emoji} ` : ''}{page.label}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">{page.value}</div>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      {/* External Link */}
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <LinkIcon className="w-4 h-4 text-blue-500" />
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>External Link</span>
                        </div>
                        <div className="relative">
                          <input
                            type="url"
                            className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                            value={taskForm.externalLink || ''}
                            onChange={(e) => setTaskForm(prev => ({ ...prev, externalLink: e.target.value }))}
                            placeholder="https://example.com"
                          />
                          {taskForm.externalLink && (
                            <button
                              onClick={() => {
                                setTaskForm(prev => ({ ...prev, externalLink: '' }));
                              }}
                              className="absolute right-2 top-2 text-gray-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Call Booking Link */}
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <Phone className="w-4 h-4 text-red-500" />
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Call Booking</span>
                        </div>
                        <div className="relative">
                          <select
                            className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                            value={taskForm.callBookingDepartment || ''}
                            onChange={(e) => setTaskForm(prev => ({ ...prev, callBookingDepartment: e.target.value }))}
                          >
                            <option value="">None</option>
                            <option value="sales">Sales</option>
                            <option value="operations">Operations</option>
                            <option value="technology">Technology</option>
                            <option value="finance">Finance</option>
                          </select>
                          {taskForm.callBookingDepartment && (
                            <button
                              type="button"
                              onClick={() => setTaskForm(prev => ({ ...prev, callBookingDepartment: '' }))}
                              className="absolute right-2 top-2 text-gray-400 hover:text-red-500"
                              title="Clear"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={handleCancelTask}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    >
                      Cancel
                    </button>
                    <SaveButton onClick={handleSaveTask} />
                  </div>
                </div>
              </div>
            )}

            {/* Tasks List */}
            {template.tasks && template.tasks.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {template.tasks.map((task, index) => (
                  <div key={task.id || index} className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 border-2 border-blue-500 relative`}>
                    <div className="flex items-start justify-between mb-2">
                      <h3 className={`font-semibold text-base ${darkMode ? 'text-white' : 'text-gray-900'} flex-1 pr-2`}>
                        {task.title}
                      </h3>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleEditTask(index)}
                          className={`p-1 rounded hover:bg-gray-600 dark:hover:bg-gray-600 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                          title="Edit task"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteTask(index)}
                          className={`p-1 rounded hover:bg-red-100 dark:hover:bg-red-900 ${darkMode ? 'text-red-400' : 'text-red-600'}`}
                          title="Delete task"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {task.description && (
                      <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-3`}>
                        {task.description}
                      </p>
                    )}
                    
                    {/* Links Section */}
                    <div className="space-y-2 mt-3">
                      {task.courseTitle && (
                        <div className={`flex items-center space-x-2 text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                          <BookOpen className="w-3 h-3" />
                          <span className="truncate">{task.courseTitle}</span>
                        </div>
                      )}
                      {task.portalPage && (
                        <div className={`flex items-center space-x-2 text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                          <ExternalLink className="w-3 h-3" />
                          <span className="truncate">{getPortalPageLabel(task.portalPage)}</span>
                        </div>
                      )}
                      {task.callBookingDepartment && (
                        <div className={`flex items-center space-x-2 text-xs ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                          <Phone className="w-3 h-3" />
                          <span className="truncate">
                            Call Booking ({task.callBookingDepartment})
                          </span>
                        </div>
                      )}
                      {task.externalLink && (
                        <a
                          href={task.externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center space-x-2 text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'} hover:underline`}
                        >
                          <LinkIcon className="w-3 h-3" />
                          <span className="truncate">{task.externalLink}</span>
                        </a>
                      )}
                    </div>

                    {/* Order Controls */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleMoveTask(index, 'up')}
                          disabled={index === 0}
                          className={`p-1 rounded ${index === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-600 dark:hover:bg-gray-600'} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                          title="Move up"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleMoveTask(index, 'down')}
                          disabled={index === template.tasks.length - 1}
                          className={`p-1 rounded ${index === template.tasks.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-600 dark:hover:bg-gray-600'} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                          title="Move down"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        #{task.order || index + 1}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckSquare className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  No tasks defined for this template
                </p>
              </div>
            )}
        </div>

        {/* Emoji Picker Modal */}
        {showEmojiPicker && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowEmojiPicker(false)}>
            <div 
              className={`bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Select Emoji</h3>
                <button
                  onClick={() => {
                    setShowEmojiPicker(false);
                    setEmojiSearch('');
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search emojis..."
                  value={emojiSearch}
                  onChange={(e) => setEmojiSearch(e.target.value)}
                  className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md`}
                />
              </div>

              <div className="overflow-y-auto flex-1">
                <div className="grid grid-cols-8 gap-4 p-4">
                  {filteredEmojis.map((emoji, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setEditForm(prev => ({ ...prev, emoji }));
                        setShowEmojiPicker(false);
                        setEmojiSearch('');
                      }}
                      className={`p-3 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} focus:outline-none focus:ring-2 focus:ring-red-500 text-2xl ${
                        editForm.emoji === emoji 
                          ? 'bg-red-600 text-white' 
                          : darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmojiPicker(false);
                    setEmojiSearch('');
                  }}
                  className={`px-4 py-2 ${darkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'} text-white rounded-md`}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoadmapTemplateOverview;

